"use client";

import { useState } from "react";
import Link from "next/link";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { post } from "@/lib/api/client";

type FormStatus = "idle" | "submitting" | "success" | "error";

const enquiryTypes = [
  { value: "general", label: "General" },
  { value: "partnership", label: "Partnership" },
  { value: "concern", label: "Report a Concern" },
  { value: "feedback", label: "Feedback" },
];

const CONTACT_TO_EMAIL =
  process.env.NEXT_PUBLIC_CONTACT_EMAIL ?? "crewsahead@gmail.com";

function buildMailtoUrl(
  name: string,
  email: string,
  enquiryType: string,
  message: string
): string {
  const subject = encodeURIComponent(
    `[CrewAhead] ${enquiryType.charAt(0).toUpperCase() + enquiryType.slice(1)} from ${name}`
  );
  const body = encodeURIComponent(
    `Name: ${name}\nEmail: ${email}\nSubject: ${enquiryType}\n\n${message}`
  );
  return `mailto:${CONTACT_TO_EMAIL}?subject=${subject}&body=${body}`;
}

interface ContactFormProps {
  defaultSubject?: string;
  showFraudCallout?: boolean;
}

export default function ContactForm({
  defaultSubject = "general",
  showFraudCallout = false,
}: ContactFormProps) {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [mailtoUrl, setMailtoUrl] = useState<string | null>(null);
  const [subject, setSubject] = useState(defaultSubject);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("submitting");
    setError(null);
    setMailtoUrl(null);

    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = {
      name: String(formData.get("name") ?? ""),
      email: String(formData.get("email") ?? ""),
      enquiryType: String(formData.get("enquiryType") ?? defaultSubject),
      message: String(formData.get("message") ?? ""),
    };

    try {
      await post("/content/contact", {
        name: payload.name,
        email: payload.email,
        enquiry_type: payload.enquiryType,
        subject: `CrewAhead ${payload.enquiryType} enquiry`,
        message: payload.message,
      });

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setError("Unable to send your message. You can use the email link below.");
      setMailtoUrl(
        buildMailtoUrl(payload.name, payload.email, payload.enquiryType, payload.message)
      );
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-3xl border border-neon-green/20 bg-neon-green/8 p-8 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-neon-green" />
        <p className="mt-4 text-lg font-semibold">Thank you for your message.</p>
        <p className="mt-2 text-sm text-ash">
          We have received your enquiry and will respond as soon as possible.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showFraudCallout && (
        <div className="rounded-2xl border border-neon-amber/20 bg-neon-amber/8 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-neon-amber" />
            <div>
              <p className="font-semibold text-neon-amber">Report suspicious recruitment activity</p>
              <p className="mt-1 text-sm text-ash">
                If something feels wrong — requests for payment, fake agencies, or unclear contracts — report it. Learn the{" "}
                <Link href="/for-seafarers" className="underline hover:text-neon-amber">
                  common fraud red flags
                </Link>
                .
              </p>
              <button
                type="button"
                onClick={() => setSubject("concern")}
                className="mt-3 inline-flex rounded-lg border border-neon-amber/30 px-4 py-2 text-sm font-semibold text-neon-amber hover:bg-neon-amber/10"
              >
                Pre-select “Report a Concern”
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="rounded-3xl border border-neon-cyan/15 bg-panel/70 p-8 shadow-sm">
        <div className="grid gap-6 sm:grid-cols-2">
          <label className="block text-sm font-medium">
            Name
            <input
              name="name"
              required
              minLength={2}
              autoComplete="name"
              className="mt-2 w-full rounded-lg border border-white/10 bg-obsidian px-4 py-3 text-white outline-none focus:border-neon-cyan"
            />
          </label>
          <label className="block text-sm font-medium">
            Email
            <input
              name="email"
              type="email"
              required
              autoComplete="email"
              className="mt-2 w-full rounded-lg border border-white/10 bg-obsidian px-4 py-3 text-white outline-none focus:border-neon-cyan"
            />
          </label>
        </div>
        <label className="mt-6 block text-sm font-medium">
          Subject
          <select
            name="enquiryType"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-2 w-full rounded-lg border border-white/10 bg-obsidian px-4 py-3 text-white outline-none focus:border-neon-cyan"
          >
            {enquiryTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </label>
        <label className="mt-6 block text-sm font-medium">
          Message
          <textarea
            name="message"
            required
            minLength={10}
            rows={6}
            className="mt-2 w-full rounded-lg border border-white/10 bg-obsidian px-4 py-3 text-white outline-none focus:border-neon-cyan"
          />
        </label>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-lg bg-neon-cyan px-6 py-3 text-sm font-semibold text-obsidian hover:bg-neon-cyan/90 disabled:opacity-60"
        >
          {status === "submitting" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Sending
            </>
          ) : (
            "Send message"
          )}
        </button>
      </form>

      {error && (
        <div className="rounded-2xl border border-red-500/20 bg-red-500/5 p-5">
          <p className="text-sm font-medium text-red-400">{error}</p>
          {mailtoUrl && (
            <p className="mt-3 text-sm">
              <a
                href={mailtoUrl}
                className="font-semibold underline hover:text-red-300"
              >
                Open your email client to send this message
              </a>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
